<template>
  <div class="grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch">
    <FormStepInputs
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #personalDetailsTitle>
        <div class="mt-4">
          <PanelTitle v-bind="getFormInput(props.content, 'personalDetailsTitle')"></PanelTitle>
        </div>
      </template>
      <template #personalDetails>
        <div class="md:w-176 mt-2">
          <SectionPanel
            :ssGreyBg="{ value: true }"
            :title="{ value: getDisplayName() }"
            :description="{
              value: fillBookmarksPersonalDetailsContent(
                getFormInput(props.content, 'personalDetailsContent')?.label?.value
              ),
            }"
          >
          </SectionPanel>
        </div>
      </template>
      <template #contactAddressInformation>
        <div class="mt-3">
          <LinkPanel
            :bindings="contactAddressInformation.formInputId?.value"
            :fields="contactAddressInformation"
            :heading="contactAddressInformation.heading"
            :headingDescription="contactAddressInformation.headingDescription"
            :links="fillContactInformationBookmarks(contactAddressInformation.fields)"
            :subheading="contactAddressInformation.subheading"
            :subheadingDescription="contactAddressInformation.subheadingDescription"
            :isGreyBackground="contactAddressInformation.isGreyBackground"
            :isArrowLinks="contactAddressInformation.isArrowLinks"
            :iconSize="contactAddressInformation.iconSize"
            :globalIcon="contactAddressInformation.globalIcon"
            :globalButtonLabel="contactAddressInformation.globalButtonLabel"
            :handleLinkClickEvent="false"
          ></LinkPanel>
        </div>
      </template>
      <template #accountDetails>
        <div class="mt-10">
          <LinkPanel
            :bindings="accountDetails.formInputId?.value"
            :fields="accountDetails"
            :heading="accountDetails.heading"
            :headingDescription="accountDetails.headingDescription"
            :links="accountDetails.fields"
            :subheading="accountDetails.subheading"
            :subheadingDescription="accountDetails.subheadingDescription"
            :isGreyBackground="accountDetails.isGreyBackground"
            :isArrowLinks="accountDetails.isArrowLinks"
            :iconSize="accountDetails.iconSize"
            :globalIcon="accountDetails.globalIcon"
            :globalButtonLabel="accountDetails.globalButtonLabel"
            :handleLinkClickEvent="false"
          ></LinkPanel>
        </div>
      </template>
      <template #accountPreferences>
        <div class="mt-10">
          <LinkPanel
            :bindings="accountPreferences.formInputId?.value"
            :fields="accountPreferences"
            :heading="accountPreferences.heading"
            :headingDescription="accountPreferences.headingDescription"
            :links="accountPreferences.fields"
            :subheading="accountPreferences.subheading"
            :subheadingDescription="accountPreferences.subheadingDescription"
            :isGreyBackground="accountPreferences.isGreyBackground"
            :isArrowLinks="accountPreferences.isArrowLinks"
            :iconSize="accountPreferences.iconSize"
            :globalIcon="accountPreferences.globalIcon"
            :globalButtonLabel="accountPreferences.globalButtonLabel"
            :handleLinkClickEvent="false"
          ></LinkPanel>
        </div>
        <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
          <Divider></Divider>
        </div>
      </template>
    </FormStepInputs>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import { useAccountStore } from '@/store';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import LinkPanel from '@/components/LinkPanel/LinkPanel.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { formatDate } from 'date-fns';
import { toProperCase } from '@/utils/text-transforms';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import SectionPanel from '@/components/SectionPanel/SectionPanel.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import Divider from '@/components/Divider/Divider.vue';

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const copyMemberNumberContentElement = 'copyMemberNumber';

const toastAlertStore = useToastAlertStore();
const accountStore = useAccountStore();
const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

const contactAddressInformation = ref(getFormInput(props.content, 'contactAddressInformation'));
const accountDetails = ref(getFormInput(props.content, 'accountDetails'));
const accountPreferences = ref(getFormInput(props.content, 'accountPreferences'));

enum PersonalDetailsBookmarks {
  memberFullName = 'memberFullName',
  memberDateOfBirth = 'memberDateOfBirth',
  memberNumber = 'memberNumber',
}

enum ContactInformationBookmarks {
  addressTypesText = 'addressTypesText',
}

onMounted(() => {
  const copyElement = document.getElementById(copyMemberNumberContentElement);
  if (copyElement) {
    copyElement.replaceWith(copyElement.cloneNode(true));

    const freshCopyElement = document.getElementById(copyMemberNumberContentElement);

    if (freshCopyElement) {
      freshCopyElement.addEventListener('click', () => {
        navigator.clipboard
          .writeText(accountStore.identityDetails?.membershipCardNumber?.replaceAll(' ', '') ?? '')
          .then(() => {
            toastAlertStore.addToastAlert({
              title: 'Member number copied',
              description: '',
              type: ToastAlertType.SUCCESS,
              duration: 6000,
            });
          });
      });
    }
  }

  pushAnalytics();
});

function getDisplayName() {
  return `${toProperCase(accountStore.identityDetails?.title)} ${toProperCase(accountStore.identityDetails?.firstName)} ${toProperCase(accountStore.identityDetails?.lastName)}`;
}

function fillBookmarksPersonalDetailsContent(content: string) {
  if (content) {
    content = replaceBookmark(
      PersonalDetailsBookmarks.memberFullName,
      content,
      `${toProperCase(accountStore.identityDetails?.title)} ${toProperCase(accountStore.identityDetails?.firstName)} ${toProperCase(accountStore.identityDetails?.lastName)}`
    );

    content = replaceBookmark(
      PersonalDetailsBookmarks.memberDateOfBirth,
      content,
      formatDate(new Date(accountStore.identityDetails?.birthDate ?? ''), STANDARD_DATE_FORMAT)
    );

    content = replaceBookmark(
      PersonalDetailsBookmarks.memberNumber,
      content,
      accountStore.identityDetails?.membershipCardNumber ?? ''
    );
  }

  return content;
}

function fillContactInformationBookmarks(fields: any[]) {
  fields.forEach((field) => {
    if (field.subTitle?.value) {
      const textElements = ['Residential', 'postal', 'insured'];
      let addressesText = '';

      if ((accountStore.identityDetails?.portfolio?.insurancePolicies?.length ?? 0) > 0) {
        addressesText = `${textElements[0]}, ${textElements[1]} and ${textElements[2]} addresses`;
      } else {
        addressesText = `${textElements[0]} and ${textElements[1]} addresses`;
      }

      field.subTitle.value = replaceBookmark(
        ContactInformationBookmarks.addressTypesText,
        field.subTitle?.value,
        addressesText
      );
    }
  });
  return fields;
}

const pushAnalytics = async () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
